import React, { createContext, useContext, useState, useEffect } from 'react';

const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

// Translations object
const translations = {
  es: {
    // Navigation
    nav: {
      home: 'Inicio',
      cabins: 'Cabañas',
      book: 'Reservar',
      mazamitla: 'Mazamitla',
      contact: 'Contacto',
    },
    // Hero section
    hero: {
      subtitle: 'Mazamitla, Pueblo Mágico',
      title: 'Tu refugio en la montaña',
      description: 'Escapa de la rutina y descubre la tranquilidad de nuestras cabañas en el corazón de Mazamitla.',
      bookNow: 'Reservar Ahora',
      viewCabins: 'Ver Cabañas',
    },
    // Cabins section & page
    cabins: {
      subtitle: 'Nuestras Cabañas',
      title: 'Comodidad y naturaleza',
      description: 'Dos modelos únicos diseñados para brindarte la mejor experiencia. Cada cabaña cuenta con todas las comodidades para una estancia inolvidable.',
      pageDescription: 'Contamos con 4 cabañas disponibles: 2 grandes para grupos de hasta 10 personas y 2 medianas ideales para parejas o familias pequeñas.',
      available: 'disponibles',
      large: 'Grande',
      medium: 'Mediana',
      largeCabin: 'Cabaña Grande',
      mediumCabin: 'Cabaña Mediana',
      perNight: 'por noche',
      guests: 'huéspedes',
      guest: 'huésped',
      bedrooms: 'habitaciones',
      bedroom: 'habitación',
      bathrooms: 'baños',
      bathroom: 'baño',
      beds: 'camas',
      viewDetails: 'Ver detalles',
      book: 'Reservar',
      viewAll: 'Ver las 4 cabañas disponibles',
      cabinDescription: 'Nueva cabaña en un fraccionamiento exclusivo que cuenta con velador 24/7. Hermosa vista para pasar un rato ameno con la familia. Es el lugar indicado para relajarte y despejarte.',
      backToCabins: 'Volver a cabañas',
      bookThisCabin: 'Reservar esta cabaña',
      checkAvailability: 'Consultar disponibilidad',
      amenities: 'Amenidades',
      filterAll: 'Todas',
      filterLarge: 'Grandes',
      filterMedium: 'Medianas',
    },
    // Features
    features: {
      wifi: 'WiFi Gratis',
      wifiDesc: 'Conexión de alta velocidad en todas las cabañas',
      fireplace: 'Chimenea',
      fireplaceDesc: 'Disfruta del calor y ambiente acogedor',
      parking: 'Estacionamiento',
      parkingDesc: 'Espacio seguro para tu vehículo',
      security: 'Seguridad 24/7',
      securityDesc: 'Vigilancia permanente en el fraccionamiento',
    },
    // Mazamitla section & page
    mazamitla: {
      subtitle: 'Descubre Mazamitla',
      title: 'Pueblo Mágico',
      description: 'Explora los encantos de este hermoso pueblo rodeado de bosques de pino, cascadas y tradiciones.',
      exploreMore: 'Explorar más de Mazamitla',
      pageSubtitle: 'Destino turístico',
      pageTitle: 'Descubre Mazamitla',
      pageDescription: 'Conocido como "Pueblo Mágico", Mazamitla te ofrece paisajes de ensueño, gastronomía tradicional y actividades para toda la familia.',
      pointsOfInterest: 'Puntos de interés',
      activities: 'Actividades',
      gastronomy: 'Gastronomía',
      weather: 'Clima',
      weatherDesc: 'Clima templado durante todo el año. Temperaturas entre 10°C y 25°C.',
      bestTime: 'Mejor época',
      bestTimeDesc: 'Todo el año es bueno para visitar, pero la temporada de lluvias (junio-septiembre) ofrece paisajes más verdes.',
    },
    // CTA section
    cta: {
      title: '¿Listo para tu escapada?',
      description: 'Reserva ahora y vive momentos inolvidables con tu familia y amigos en el corazón de Mazamitla.',
      bookNow: 'Reservar Ahora',
      whatsapp: 'Contactar por WhatsApp',
    },
    // Reviews
    reviews: {
      title: 'Lo que dicen nuestros huéspedes',
      subtitle: 'Experiencias',
      leaveReview: 'Deja tu reseña',
      yourName: 'Tu nombre',
      stayDate: 'Fecha de hospedaje',
      yourExperience: 'Cuéntanos tu experiencia',
      submit: 'Enviar Reseña',
      sending: 'Enviando...',
      thankYou: '¡Gracias por tu reseña!',
      basedOn: 'Basado en',
      reviewsText: 'reseñas',
    },
    // Contact page
    contact: {
      title: 'Contáctanos',
      subtitle: 'Estamos aquí para ayudarte',
      pageDescription: '¿Tienes preguntas sobre nuestras cabañas o quieres más información? Estamos aquí para ayudarte.',
      contactInfo: 'Información de contacto',
      name: 'Nombre',
      fullName: 'Nombre completo',
      phone: 'Teléfono',
      phoneWhatsApp: 'Teléfono / WhatsApp',
      email: 'Correo electrónico',
      emailOptional: 'Email (opcional)',
      message: 'Mensaje',
      send: 'Enviar mensaje',
      sending: 'Enviando...',
      success: '¡Mensaje enviado!',
      successDesc: 'Gracias por contactarnos. Te responderemos a la brevedad.',
      sendAnother: 'Enviar otro mensaje',
      location: 'Ubicación',
      locationDesc: 'A 5 minutos del centro del pueblo',
      whatsapp: 'WhatsApp',
      whatsappDesc: 'La forma más rápida de contactarnos',
      schedule: 'Horario de atención',
      scheduleDesc: 'Lunes a Domingo',
      scheduleHours: '9:00 AM - 9:00 PM',
      readyToBook: '¿Listo para reservar?',
      readyToBookDesc: 'Si ya decidiste tu fecha, reserva directamente en nuestro sitio.',
      goToBook: 'Ir a reservar',
      sendMessage: 'Envíanos un mensaje',
      writeMessage: 'Escribe tu mensaje aquí...',
      requiredFields: 'Por favor completa los campos requeridos',
      errorSending: 'Error al enviar el mensaje. Intenta de nuevo.',
      // FAQ
      faq: 'Preguntas frecuentes',
      faqCheckIn: '¿A qué hora es el check-in y check-out?',
      faqCheckInAnswer: 'El check-in es a partir de las 3:00 PM y el check-out hasta las 12:00 PM. Podemos ajustar horarios según disponibilidad.',
      faqPets: '¿Se aceptan mascotas?',
      faqPetsAnswer: 'Consulta con nosotros. Algunas cabañas permiten mascotas con previo aviso y ciertas condiciones.',
      faqPayment: '¿Cómo puedo pagar mi reservación?',
      faqPaymentAnswer: 'Aceptamos transferencia bancaria. El 50% se paga al reservar y el resto antes del check-in. Recibirás la confirmación por WhatsApp una vez verificado el pago.',
      faqIncludes: '¿Qué incluye la reservación?',
      faqIncludesAnswer: 'Todas las cabañas incluyen ropa de cama, toallas, cocina equipada, WiFi, estacionamiento y acceso al fraccionamiento.',
    },
    // Booking page
    booking: {
      title: 'Reserva tu cabaña',
      subtitle: 'Reservaciones',
      selectCabin: 'Selecciona tu cabaña',
      selectDates: 'Selecciona fechas',
      cabin: 'Cabaña',
      selectACabin: 'Selecciona una cabaña',
      stayDates: 'Fechas de estancia',
      checkIn: 'Check-in',
      checkOut: 'Check-out',
      numberOfGuests: 'Número de huéspedes',
      guests: 'Huéspedes',
      guestData: 'Datos del huésped',
      fullName: 'Nombre completo',
      yourName: 'Tu nombre',
      phone: 'Teléfono / WhatsApp',
      email: 'Email (opcional)',
      notes: 'Notas adicionales (opcional)',
      notesPlaceholder: 'Hora aproximada de llegada, solicitudes especiales...',
      paymentMethod: 'Método de pago',
      transfer: 'Transferencia bancaria',
      transferDesc: 'Te enviaremos los datos por WhatsApp',
      paypal: 'PayPal',
      paypalDesc: 'Pago seguro con tarjeta o cuenta PayPal',
      summary: 'Resumen',
      bookingSummary: 'Resumen de reservación',
      basePricePerNight: 'Precio base por noche',
      averagePrice: 'Precio promedio (temporada)',
      nights: 'Noches',
      seasonRates: 'Tarifas de temporada aplicadas:',
      total: 'Total',
      totalToPay: 'Total a pagar',
      continue: 'Continuar',
      confirmBooking: 'Confirmar reservación',
      processing: 'Procesando...',
      back: 'Volver',
      selectCabinToSee: 'Selecciona una cabaña para ver el resumen',
      // Step labels
      stepSelect: 'Seleccionar',
      stepData: 'Datos',
      stepConfirmation: 'Confirmación',
      // Confirmation
      confirmed: '¡Reservación registrada!',
      confirmedDesc: 'Tu solicitud de reservación ha sido registrada exitosamente.',
      bookingDetails: 'Detalles de tu reservación',
      confirmationNumber: 'Número de confirmación',
      nextStep: '¿Qué sigue?',
      nextStepDesc: 'Te enviaremos los datos bancarios por WhatsApp para que puedas realizar la transferencia. Una vez confirmado tu pago, recibirás la confirmación definitiva de tu reservación por WhatsApp.',
      whatsappConfirmation: 'Recibirás la confirmación de tu reservación por WhatsApp una vez que verifiquemos tu pago.',
      contactWhatsApp: 'Contactar por WhatsApp',
      backToHome: 'Volver al inicio',
      bookingSuccess: '¡Reservación creada exitosamente!',
      bookingError: 'Error al crear la reservación',
      loadError: 'Error al cargar las cabañas',
    },
    // Footer
    footer: {
      description: 'Tu refugio perfecto en el corazón de Mazamitla. Vive una experiencia única rodeado de naturaleza.',
      quickLinks: 'Enlaces rápidos',
      contact: 'Contacto',
      rights: 'Todos los derechos reservados',
      readyToEscape: '¿Listo para escapar?',
      bookToday: 'Reserva tu cabaña hoy y vive la experiencia Mazamitla.',
      privacyPolicy: 'Política de Privacidad',
      terms: 'Términos y Condiciones',
    },
    // Admin
    admin: {
      panel: 'Panel de Administración',
      password: 'Contraseña',
      enterPassword: 'Ingresa tu contraseña',
      login: 'Ingresar',
      loggingIn: 'Ingresando...',
      logout: 'Salir',
      reload: 'Recargar',
      viewSite: 'Ver sitio',
      overview: 'Resumen',
      bookings: 'Reservas',
      rates: 'Tarifas',
      photos: 'Fotos',
      reviews: 'Reseñas',
      messages: 'Mensajes',
      settings: 'Configuración',
      totalBookings: 'Total Reservaciones',
      pending: 'Pendientes',
      paid: 'Pagadas',
      revenue: 'Ingresos',
      recentBookings: 'Reservaciones Recientes',
      noBookings: 'No hay reservaciones',
      noReviews: 'No hay reseñas',
      noMessages: 'No hay mensajes',
      delete: 'Eliminar',
      cancel: 'Cancelar',
      confirm: 'Confirmar',
      approve: 'Aprobar',
      reject: 'Rechazar',
      deleteConfirm: '¿Estás seguro de que deseas eliminar este elemento?',
      deleteWarning: 'Esta acción no se puede deshacer.',
      status: 'Estado',
      actions: 'Acciones',
      guest: 'Huésped',
      dates: 'Fechas',
      amount: 'Monto',
      paymentStatus: 'Estado de pago',
      markAsPaid: 'Marcar como pagada',
      // Settings
      contactData: 'Datos de contacto',
      businessHours: 'Horario de atención',
      bankData: 'Datos bancarios',
      changePassword: 'Cambiar contraseña',
      currentPassword: 'Contraseña actual',
      newPassword: 'Nueva contraseña',
      confirmPassword: 'Confirmar contraseña',
      save: 'Guardar',
      saving: 'Guardando...',
    },
    // Common
    common: {
      loading: 'Cargando...',
      error: 'Error',
      success: 'Éxito',
      save: 'Guardar',
      cancel: 'Cancelar',
      delete: 'Eliminar',
      edit: 'Editar',
      view: 'Ver',
      close: 'Cerrar',
      yes: 'Sí',
      no: 'No',
      required: 'requerido',
      optional: 'opcional',
      mxn: 'MXN',
    },
  },
  en: {
    // Navigation
    nav: {
      home: 'Home',
      cabins: 'Cabins',
      book: 'Book',
      mazamitla: 'Mazamitla',
      contact: 'Contact',
    },
    // Hero section
    hero: {
      subtitle: 'Mazamitla, Magic Town',
      title: 'Your mountain retreat',
      description: 'Escape the routine and discover the tranquility of our cabins in the heart of Mazamitla.',
      bookNow: 'Book Now',
      viewCabins: 'View Cabins',
    },
    // Cabins section & page
    cabins: {
      subtitle: 'Our Cabins',
      title: 'Comfort and nature',
      description: 'Two unique models designed to give you the best experience. Each cabin has all the amenities for an unforgettable stay.',
      pageDescription: 'We have 4 cabins available: 2 large cabins for groups of up to 10 people and 2 medium cabins ideal for couples or small families.',
      available: 'available',
      large: 'Large',
      medium: 'Medium',
      largeCabin: 'Large Cabin',
      mediumCabin: 'Medium Cabin',
      perNight: 'per night',
      guests: 'guests',
      guest: 'guest',
      bedrooms: 'bedrooms',
      bedroom: 'bedroom',
      bathrooms: 'bathrooms',
      bathroom: 'bathroom',
      beds: 'beds',
      viewDetails: 'View details',
      book: 'Book',
      viewAll: 'View all 4 available cabins',
      cabinDescription: 'New cabin in an exclusive gated community with 24/7 security. Beautiful views to spend quality time with family. The perfect place to relax and unwind.',
      backToCabins: 'Back to cabins',
      bookThisCabin: 'Book this cabin',
      checkAvailability: 'Check availability',
      amenities: 'Amenities',
      filterAll: 'All',
      filterLarge: 'Large',
      filterMedium: 'Medium',
    },
    // Features
    features: {
      wifi: 'Free WiFi',
      wifiDesc: 'High-speed connection in all cabins',
      fireplace: 'Fireplace',
      fireplaceDesc: 'Enjoy the warmth and cozy atmosphere',
      parking: 'Parking',
      parkingDesc: 'Safe space for your vehicle',
      security: '24/7 Security',
      securityDesc: 'Permanent surveillance in the gated community',
    },
    // Mazamitla section & page
    mazamitla: {
      subtitle: 'Discover Mazamitla',
      title: 'Magic Town',
      description: 'Explore the charms of this beautiful town surrounded by pine forests, waterfalls, and traditions.',
      exploreMore: 'Explore more of Mazamitla',
      pageSubtitle: 'Tourist destination',
      pageTitle: 'Discover Mazamitla',
      pageDescription: 'Known as "Magic Town", Mazamitla offers you dreamy landscapes, traditional gastronomy and activities for the whole family.',
      pointsOfInterest: 'Points of interest',
      activities: 'Activities',
      gastronomy: 'Gastronomy',
      weather: 'Weather',
      weatherDesc: 'Temperate climate year-round. Temperatures between 50°F and 77°F.',
      bestTime: 'Best time to visit',
      bestTimeDesc: 'Year-round is good to visit, but the rainy season (June-September) offers greener landscapes.',
    },
    // CTA section
    cta: {
      title: 'Ready for your getaway?',
      description: 'Book now and live unforgettable moments with your family and friends in the heart of Mazamitla.',
      bookNow: 'Book Now',
      whatsapp: 'Contact via WhatsApp',
    },
    // Reviews
    reviews: {
      title: 'What our guests say',
      subtitle: 'Experiences',
      leaveReview: 'Leave a review',
      yourName: 'Your name',
      stayDate: 'Stay date',
      yourExperience: 'Tell us about your experience',
      submit: 'Submit Review',
      sending: 'Sending...',
      thankYou: 'Thank you for your review!',
      basedOn: 'Based on',
      reviewsText: 'reviews',
    },
    // Contact page
    contact: {
      title: 'Contact Us',
      subtitle: "We're here to help",
      pageDescription: 'Have questions about our cabins or want more information? We are here to help.',
      contactInfo: 'Contact information',
      name: 'Name',
      fullName: 'Full name',
      phone: 'Phone',
      phoneWhatsApp: 'Phone / WhatsApp',
      email: 'Email',
      emailOptional: 'Email (optional)',
      message: 'Message',
      send: 'Send message',
      sending: 'Sending...',
      success: 'Message sent!',
      successDesc: 'Thank you for contacting us. We will respond shortly.',
      sendAnother: 'Send another message',
      location: 'Location',
      locationDesc: '5 minutes from downtown',
      whatsapp: 'WhatsApp',
      whatsappDesc: 'The fastest way to contact us',
      schedule: 'Business hours',
      scheduleDesc: 'Monday to Sunday',
      scheduleHours: '9:00 AM - 9:00 PM',
      readyToBook: 'Ready to book?',
      readyToBookDesc: 'If you have already decided your date, book directly on our site.',
      goToBook: 'Go to book',
      sendMessage: 'Send us a message',
      writeMessage: 'Write your message here...',
      requiredFields: 'Please complete the required fields',
      errorSending: 'Error sending message. Please try again.',
      // FAQ
      faq: 'Frequently asked questions',
      faqCheckIn: 'What time is check-in and check-out?',
      faqCheckInAnswer: 'Check-in is from 3:00 PM and check-out is until 12:00 PM. We can adjust times based on availability.',
      faqPets: 'Are pets allowed?',
      faqPetsAnswer: 'Please contact us. Some cabins allow pets with prior notice and certain conditions.',
      faqPayment: 'How can I pay for my reservation?',
      faqPaymentAnswer: 'We accept bank transfer. 50% is paid when booking and the rest before check-in. You will receive confirmation via WhatsApp once payment is verified.',
      faqIncludes: 'What does the reservation include?',
      faqIncludesAnswer: 'All cabins include bed linen, towels, equipped kitchen, WiFi, parking and access to the gated community.',
    },
    // Booking page
    booking: {
      title: 'Book your cabin',
      subtitle: 'Reservations',
      selectCabin: 'Select your cabin',
      selectDates: 'Select dates',
      cabin: 'Cabin',
      selectACabin: 'Select a cabin',
      stayDates: 'Stay dates',
      checkIn: 'Check-in',
      checkOut: 'Check-out',
      numberOfGuests: 'Number of guests',
      guests: 'Guests',
      guestData: 'Guest information',
      fullName: 'Full name',
      yourName: 'Your name',
      phone: 'Phone / WhatsApp',
      email: 'Email (optional)',
      notes: 'Additional notes (optional)',
      notesPlaceholder: 'Approximate arrival time, special requests...',
      paymentMethod: 'Payment method',
      transfer: 'Bank transfer',
      transferDesc: 'We will send you the details via WhatsApp',
      paypal: 'PayPal',
      paypalDesc: 'Secure payment with card or PayPal account',
      summary: 'Summary',
      bookingSummary: 'Booking summary',
      basePricePerNight: 'Base price per night',
      averagePrice: 'Average price (season)',
      nights: 'Nights',
      seasonRates: 'Season rates applied:',
      total: 'Total',
      totalToPay: 'Total to pay',
      continue: 'Continue',
      confirmBooking: 'Confirm booking',
      processing: 'Processing...',
      back: 'Back',
      selectCabinToSee: 'Select a cabin to see the summary',
      // Step labels
      stepSelect: 'Select',
      stepData: 'Details',
      stepConfirmation: 'Confirmation',
      // Confirmation
      confirmed: 'Booking registered!',
      confirmedDesc: 'Your reservation request has been successfully registered.',
      bookingDetails: 'Your booking details',
      confirmationNumber: 'Confirmation number',
      nextStep: "What's next?",
      nextStepDesc: 'We will send you the bank details via WhatsApp so you can make the transfer. Once your payment is confirmed, you will receive the final confirmation of your reservation via WhatsApp.',
      whatsappConfirmation: 'You will receive your booking confirmation via WhatsApp once we verify your payment.',
      contactWhatsApp: 'Contact via WhatsApp',
      backToHome: 'Back to home',
      bookingSuccess: 'Booking created successfully!',
      bookingError: 'Error creating booking',
      loadError: 'Error loading cabins',
    },
    // Footer
    footer: {
      description: 'Your perfect retreat in the heart of Mazamitla. Live a unique experience surrounded by nature.',
      quickLinks: 'Quick links',
      contact: 'Contact',
      rights: 'All rights reserved',
      readyToEscape: 'Ready to escape?',
      bookToday: 'Book your cabin today and live the Mazamitla experience.',
      privacyPolicy: 'Privacy Policy',
      terms: 'Terms and Conditions',
    },
    // Admin
    admin: {
      panel: 'Admin Panel',
      password: 'Password',
      enterPassword: 'Enter your password',
      login: 'Login',
      loggingIn: 'Logging in...',
      logout: 'Logout',
      reload: 'Reload',
      viewSite: 'View site',
      overview: 'Overview',
      bookings: 'Bookings',
      rates: 'Rates',
      photos: 'Photos',
      reviews: 'Reviews',
      messages: 'Messages',
      settings: 'Settings',
      totalBookings: 'Total Bookings',
      pending: 'Pending',
      paid: 'Paid',
      revenue: 'Revenue',
      recentBookings: 'Recent Bookings',
      noBookings: 'No bookings',
      noReviews: 'No reviews',
      noMessages: 'No messages',
      delete: 'Delete',
      cancel: 'Cancel',
      confirm: 'Confirm',
      approve: 'Approve',
      reject: 'Reject',
      deleteConfirm: 'Are you sure you want to delete this item?',
      deleteWarning: 'This action cannot be undone.',
      status: 'Status',
      actions: 'Actions',
      guest: 'Guest',
      dates: 'Dates',
      amount: 'Amount',
      paymentStatus: 'Payment status',
      markAsPaid: 'Mark as paid',
      // Settings
      contactData: 'Contact data',
      businessHours: 'Business hours',
      bankData: 'Bank data',
      changePassword: 'Change password',
      currentPassword: 'Current password',
      newPassword: 'New password',
      confirmPassword: 'Confirm password',
      save: 'Save',
      saving: 'Saving...',
    },
    // Common
    common: {
      loading: 'Loading...',
      error: 'Error',
      success: 'Success',
      save: 'Save',
      cancel: 'Cancel',
      delete: 'Delete',
      edit: 'Edit',
      view: 'View',
      close: 'Close',
      yes: 'Yes',
      no: 'No',
      required: 'required',
      optional: 'optional',
      mxn: 'MXN',
    },
  },
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    const saved = localStorage.getItem('ribba_language');
    return saved || 'es';
  });

  useEffect(() => {
    localStorage.setItem('ribba_language', language);
    document.documentElement.lang = language;
  }, [language]);

  const t = (key) => {
    const keys = key.split('.');
    let value = translations[language];
    
    for (const k of keys) {
      if (value && value[k] !== undefined) {
        value = value[k];
      } else {
        value = translations['es'];
        for (const k2 of keys) {
          if (value && value[k2] !== undefined) {
            value = value[k2];
          } else {
            return key;
          }
        }
        break;
      }
    }
    
    return value;
  };

  const toggleLanguage = () => {
    setLanguage(prev => prev === 'es' ? 'en' : 'es');
  };

  const value = {
    language,
    setLanguage,
    toggleLanguage,
    t,
    isSpanish: language === 'es',
    isEnglish: language === 'en',
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export default LanguageContext;
